/** Matches `gallery_photo` upload rules in `server/src/modules/storage/storage.service.ts`. */
export const GALLERY_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

/** Reject sources we won't attempt to load in the browser. */
export const GALLERY_PHOTO_MAX_SOURCE_BYTES = 50 * 1024 * 1024;

/** Longest edge for the stored gallery image — enough for full-screen viewing. */
export const GALLERY_PHOTO_MAX_EDGE = 2400;

const QUALITY_STEPS = [0.85, 0.75, 0.65, 0.55, 0.45] as const;

export class GalleryPhotoTooLargeError extends Error {
  readonly name = 'GalleryPhotoTooLargeError';
}

export class GalleryPhotoInvalidError extends Error {
  readonly name = 'GalleryPhotoInvalidError';
}

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new GalleryPhotoInvalidError());
    };
    image.src = url;
  });

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new GalleryPhotoInvalidError())),
      type,
      quality,
    );
  });

/**
 * Resize and re-encode a gallery photo before upload so churches don't store
 * multi-megapixel originals. Output is WebP when supported, otherwise JPEG.
 */
export const compressGalleryPhoto = async (file: File): Promise<{ blob: Blob; fileName: string }> => {
  if (!file.type.startsWith('image/')) {
    throw new GalleryPhotoInvalidError();
  }
  if (file.size > GALLERY_PHOTO_MAX_SOURCE_BYTES) {
    throw new GalleryPhotoTooLargeError();
  }

  const image = await loadImage(file);
  const { naturalWidth, naturalHeight } = image;
  if (naturalWidth < 1 || naturalHeight < 1) {
    throw new GalleryPhotoInvalidError();
  }

  const scale = Math.min(1, GALLERY_PHOTO_MAX_EDGE / Math.max(naturalWidth, naturalHeight));
  const width = Math.max(1, Math.round(naturalWidth * scale));
  const height = Math.max(1, Math.round(naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new GalleryPhotoInvalidError();
  context.drawImage(image, 0, 0, width, height);

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo';
  const outputTypes = canvas.toDataURL('image/webp').startsWith('data:image/webp')
    ? (['image/webp', 'image/jpeg'] as const)
    : (['image/jpeg'] as const);

  for (const type of outputTypes) {
    const extension = type === 'image/webp' ? 'webp' : 'jpg';
    for (const quality of QUALITY_STEPS) {
      const blob = await canvasToBlob(canvas, type, quality);
      if (blob.size <= GALLERY_PHOTO_MAX_BYTES) {
        return { blob, fileName: `${baseName}.${extension}` };
      }
    }
  }

  throw new GalleryPhotoTooLargeError();
};
